<script>
import BaseHypothesis from '@/components/AttendanceForm/general/summary/BaseHypothesis.vue'
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

import { COMORBIDITIES_OPTIONS, CONTRACEPCAO } from '@/components/AttendanceForm/general/constants/personalHistory'
import { PSYCHIATRIC_ANSWER_VALUES } from '@/components/AttendanceForm/general/constants/psychiatricScreening'
import { MEDICINE_CATEGORIES } from '@/constants/medicine'

import psoriasisSummaryMixin from '@/components/AttendanceForm/psoriasis/mixins/psoriasisSummaryMixin.js'

export default {
  mixins: [psoriasisSummaryMixin],
  extends: BaseHypothesis,
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey', 'attendance']),
    specificInfoPhaseData() {
      return this.getPhaseDataByKey('dados_especificos')
    },
    familyHistoryPhaseData() {
      return this.getPhaseDataByKey('investigacao_familiar')
    },
    investigationPhaseData() {
      return this.getPhaseDataByKey('investigacao_pessoal')
    },
    psychiatricPhaseData() {
      return this.getPhaseDataByKey('triagem_psiquiatrica')
    },
    medicineHistory() {
      return Object.values(this.getPhaseDataByKey('dados_especificos')?.medicines || {}) || []
    },
    currentMedicinesText() {
      let currentMedicines = this.medicineHistory.filter(
        (m) => m.periods.length !== 0 && m.periods.some((p) => !!p.start && !p.end),
      )

      if (currentMedicines.length === 0) {
        return 'nenhum'
      }

      return currentMedicines
        .map(
          (m) =>
            `${m.medicine.name.firstLetterToLowercase()}${
              m.periods.some((p) => !!p.start && !p.end)
                ? ' (desde ' + this.$util.toFormattedDate(m.periods.find((p) => !p.end).start) + ')'
                : ''
            }`,
        )
        .join(', ')
    },
    previousSistemicMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.SISTEMICOS_TRADICIONAIS)
    },
    previousImunobiologicMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.IMUNOBIOLOGICOS)
    },
    previousAntiinflamatoryMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.ANTI_INFLAMATORIOS)
    },
    previousTopicMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.TOPICOS)
    },
    previousFototerapicMedicinesText() {
      const hasFototerapicSessions = this.specificInfoPhaseData?.realizou_sessoes_fototerapia?.value

      if (!hasFototerapicSessions) {
        return 'nenhum'
      }

      return `${
        this.specificInfoPhaseData?.numero_sessoes_fototerapia?.value > 1
          ? this.specificInfoPhaseData.numero_sessoes_fototerapia.value + ' sessões'
          : this.specificInfoPhaseData.numero_sessoes_fototerapia.value + ' sessão'
      }`
    },
    comorbiditiesText() {
      if (
        !this.investigationPhaseData ||
        this.investigationPhaseData.noDisease ||
        !this.investigationPhaseData.diseases ||
        this.investigationPhaseData.diseases.length === 0
      ) {
        return null
      }

      const paragraphs = []

      paragraphs.push('Investigação Pessoal')

      const diseases = (this.investigationPhaseData.diseases || [])
        .filter((key) => key !== 'outras_comorbidades')
        .map((key) => COMORBIDITIES_OPTIONS.find((comorbidity) => comorbidity.key === key))

      if (diseases.length > 0) {
        paragraphs.push(
          `\nApresenta as seguintes comorbidades: ${diseases.map((disease) => disease?.name).join(', ')}.`,
        )
      }
      if ((this.investigationPhaseData.otherDisease || '').trim()) {
        paragraphs.push(`\nOutras: ${this.investigationPhaseData.otherDisease.trim()}.`)
      }

      if ((this.investigationPhaseData.medicine || '').trim()) {
        paragraphs.push(`\nMedicamentos em uso: ${this.investigationPhaseData.medicine.trim()}.`)
      }

      if (this.investigationPhaseData.profile) {
        if (this.investigationPhaseData.profile['cancer_pele']?.value) {
          let text = '\nCâncer de pele:'
          const cancerSubtype = this.investigationPhaseData.profile['tipo_cancer_pele']?.value
          if (cancerSubtype) {
            if (['MELANOMA', 'CARCINOMA'].includes(cancerSubtype)) {
              text += ` Sim. (${cancerSubtype.toLowerCase()})`
            } else {
              text += ` Sim. (subtipo desconhecido)`
            }
          }
          paragraphs.push(`${text}.`)
        } else {
          paragraphs.push('\nCâncer de pele: Não.')
        }

        if (this.investigationPhaseData.profile['tabagista']?.value) {
          paragraphs.push(`\nÉ tabagista:  ${this.investigationPhaseData.profile['tabagista'].value ? 'Sim.' : 'Não'}`)
        }
        if (this.investigationPhaseData.profile['etilista']?.value) {
          paragraphs.push(`\nÉ etilista: ${this.investigationPhaseData.profile['etilista'].value ? 'Sim.' : 'Não'}`)
        }
        if (this.investigationPhaseData.profile['tuberculose']?.value) {
          paragraphs.push(
            `\nJá teve tuberculose: ${this.investigationPhaseData.profile['etilista'].value ? 'Sim.' : 'Não'}`,
          )
        }
        if (this.investigationPhaseData.profile['tuberculose_latente']?.value) {
          paragraphs.push(
            `\nJá tratou tuberculose latente, teve exposição prévia ou trabalha exposto a pessoas com possível tuberculose ativa: ${
              this.investigationPhaseData.profile['tuberculose_latente'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }
        if (this.investigationPhaseData.profile['tratou_tuberculose']?.value) {
          paragraphs.push(
            `\nTratou tuberculose doença ou tuberculose latente: ${
              this.investigationPhaseData.profile['tratou_tuberculose'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile['drogas_ilicitas']?.value) {
          paragraphs.push(
            `\nUsa drogas ilícita? ${this.investigationPhaseData.profile['drogas_ilicitas'].value ? 'Sim.' : 'Não.'}`,
          )
        }

        if (this.investigationPhaseData.profile['contracepcao']?.value) {
          paragraphs.push(
            `\nContracepção:  ${this.investigationPhaseData.profile['contracepcao'].value ? 'Sim.' : 'Não.'}`,
          )
        }

        if (this.investigationPhaseData.profile['diarreia_cronica']?.value) {
          paragraphs.push(
            `\nTem diarreia crônica, dor abdominal ou outras alterações de hábito intestinal. ${
              this.investigationPhaseData.profile['diarreia_cronica'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile['uveite']?.value) {
          paragraphs.push(
            `\nTem olhos vermelhos, fotofobia, lacrimejamento ou outras alterações visuais (uveíte) ${
              this.investigationPhaseData.profile['uveite'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile['gravidez']?.value) {
          paragraphs.push(
            `\nEstá grávida ou pretende engravidar durante o tratamento? ${
              this.investigationPhaseData.profile['gravidez'].value ? 'Sim.' : 'Não.'
            }`,
          )
        }

        if (this.investigationPhaseData.profile['contracepcao']?.selected) {
          const birthcontrolOptions = this.investigationPhaseData.profile['contracepcao'].selected.map(
            (key) => CONTRACEPCAO.find((birthcontrol) => birthcontrol.key === key).name,
          )

          birthcontrolOptions.join(',  ')

          paragraphs.push(`\nMétodos Contraceptivos: ${birthcontrolOptions}`)
        }

        if (this.investigationPhaseData.profile['menarca']?.age) {
          paragraphs.push(`\nMenarca com quantos anos? ${this.investigationPhaseData.profile['menarca'].age}`)
        }
      }
      return paragraphs
    },
    familyHistoryText() {
      const paragraphs = []

      paragraphs.push('Investigacão Familiar')

      if (this.familyHistoryPhaseData.familyAtopias?.asma?.selected) {
        paragraphs.push(`\nAsma na família: ${this.familyHistoryPhaseData.familyAtopias.asma.selected ? 'Sim' : 'Não'}`)
        if (this.familyHistoryPhaseData.familyAtopias.asma.degree.length > 0) {
          paragraphs.push(
            `\nGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.asma.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }

      if (this.familyHistoryPhaseData.familyAtopias?.rinite_alergica?.selected) {
        paragraphs.push(
          `\nRinite Alérgica na família: ${
            this.familyHistoryPhaseData.familyAtopias.rinite_alergica.selected ? 'Sim' : 'Não'
          }`,
        )
        if (this.familyHistoryPhaseData.familyAtopias.rinite_alergica.degree.length > 0) {
          paragraphs.push(
            `\nGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.rinite_alergica.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }

      if (this.familyHistoryPhaseData.familyAtopias?.dermatite_atopica?.selected) {
        paragraphs.push(
          `\nDermatite Atopica na família: ${
            this.familyHistoryPhaseData.familyAtopias.dermatite_atopica.selected ? 'Sim' : 'Não'
          }`,
        )
        if (this.familyHistoryPhaseData.familyAtopias.dermatite_atopica.degree.length > 0) {
          paragraphs.push(
            `\nGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.dermatite_atopica.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }

      if (this.familyHistoryPhaseData.familyAtopias?.psoriase?.selected) {
        paragraphs.push(
          `\nPsoríase na família: ${this.familyHistoryPhaseData.familyAtopias.psoriase.selected ? 'Sim' : 'Não'}`,
        )
        if (this.familyHistoryPhaseData.familyAtopias.psoriase.degree.length > 0) {
          paragraphs.push(
            `\nGrau de parentesco: ${this.familyHistoryPhaseData.familyAtopias.psoriase.degree
              .map((degree) => degree + ' grau')
              .join(', ')}`,
          )
        }
      }
      if (this.familyHistoryPhaseData.profile) {
        if (this.familyHistoryPhaseData.profile['doenca_reumatologia']?.value) {
          paragraphs.push(
            `\nAlguém na família tem doença reumatológica (reumatismo, artrite, doença auto-imune): ${
              this.familyHistoryPhaseData.profile['doenca_reumatologia'].value ? 'Sim' : 'Não'
            }`,
          )
          if (this.familyHistoryPhaseData.profile['doenca_reumatologia'].complement) {
            paragraphs.push(
              `\nDoença informada: ${this.familyHistoryPhaseData.profile['doenca_reumatologia'].complement}`,
            )
          }
          if (this.familyHistoryPhaseData.profile['doenca_reumatologia'].degree) {
            paragraphs.push(
              `\nGrau de parentesco: ${this.familyHistoryPhaseData.profile['doenca_reumatologia'].degree}`,
            )
          }
        }

        if (this.familyHistoryPhaseData.profile['esclerose_multipla']?.value) {
          paragraphs.push(
            `\nAlguém na família tem esclerose mútlipla: ${
              this.familyHistoryPhaseData.profile['esclerose_multipla'].value ? 'Sim' : 'Não'
            }`,
          )
          if (this.familyHistoryPhaseData.profile['esclerose_multipla'].complement) {
            paragraphs.push(
              `\nDoença informada: ${this.familyHistoryPhaseData.profile['esclerose_multipla'].complement}`,
            )
          }
          if (this.familyHistoryPhaseData.profile['esclerose_multipla'].degree) {
            paragraphs.push(`\nGrau de parentesco: ${this.familyHistoryPhaseData.profile['esclerose_multipla'].degree}`)
          }
        }

        if (this.familyHistoryPhaseData.profile['cancer_familia']?.value) {
          paragraphs.push(
            `\nAlgum histórico de câncer na família: ${
              this.familyHistoryPhaseData.profile['cancer_familia'].value ? 'Sim' : 'Não'
            }`,
          )
          if (this.familyHistoryPhaseData.profile['cancer_familia'].complement) {
            paragraphs.push(`\nDoença informada: ${this.familyHistoryPhaseData.profile['cancer_familia'].complement}`)
          }
          if (this.familyHistoryPhaseData.profile['cancer_familia'].degree) {
            paragraphs.push(`\nGrau de parentesco: ${this.familyHistoryPhaseData.profile['cancer_familia'].degree}`)
          }
        }
      }

      return paragraphs.join('')
    },

    previousTuberculosisText() {
      let previousTuberculosis = this.investigationPhaseData?.profile?.tuberculose?.value != 'NÃO' ?? null
      if (previousTuberculosis === null) {
        return 'Histórico de tuberculose prévia não respondido'
      } else if (previousTuberculosis) {
        return 'Tem histórico de tuberculose prévia'
      } else {
        return 'Não tem histórico de tuberculose prévia'
      }
    },
    tuberculosisRiskText() {
      let tuberculosisRisk = this.investigationPhaseData?.profile?.tuberculose_latente?.value ?? null
      if (tuberculosisRisk === null) {
        return 'Risco para contato com tuberculose não respondido'
      } else if (tuberculosisRisk) {
        return 'Tem alto risco para contato com tuberculose'
      } else {
        return 'Não tem alto risco para contato com tuberculose'
      }
    },
    anxietyScore() {
      const firstAnswer =
        this.psychiatricPhaseData?.profile['nervoso_ansioso_limite_duas_ultimas_semanas']?.value || 'NUNCA'
      const secondAnswer =
        this.psychiatricPhaseData?.profile['incapaz_controlar_preocupacoes_duas_ultimas_semanas']?.value || 'NUNCA'

      return PSYCHIATRIC_ANSWER_VALUES[firstAnswer] + PSYCHIATRIC_ANSWER_VALUES[secondAnswer]
    },
    depressionScore() {
      const firstAnswer =
        this.psychiatricPhaseData?.profile['pouco_interesse_prazer_duas_ultimas_semanas']?.value || 'NUNCA'
      const secondAnswer = this.psychiatricPhaseData?.profile['deprimido_duas_ultimas_semanas']?.value || 'NUNCA'

      return PSYCHIATRIC_ANSWER_VALUES[firstAnswer] + PSYCHIATRIC_ANSWER_VALUES[secondAnswer]
    },
    healthPlanText() {
      return this.attendance.healthPlan
        ? `Com acesso à rede privada ${this.attendance.healthPlan}`
        : 'Com acesso à rede pública SUS'
    },

    affectedAreasText() {
      const areasAfetadas = this.getPhaseDataByKey('areas_afetadas')?.areas_afetadas || {}
      if (Object.values(areasAfetadas).filter((a) => a.selected).length === 0) {
        return 'atualmente sem lesões'
      }

      const classes = [
        ...new Set(
          Object.values(areasAfetadas)
            .filter((a) => a.selected)
            .map((a) => a.classKey.toLowerCase()),
        ),
      ]
      return 'com o(s) subtipo(s): ' + classes.join(', ')
    },
    diseaseLevelText() {
      if (
        Number(this.computedPasi) >= 10 ||
        Number(this.computedBsa) >= 10 ||
        Number(this.computedDlqi) >= 10 ||
        this.getPhaseDataByKey('areas_afetadas')?.medicamentos_previos_mais_de_3_meses === 'SIM_SEM_MELHORIA'
      ) {
        return 'moderada a grave'
      }
      return 'leve'
    },
    diagnosisTimeText() {
      if (!this.getPhaseDataByKey('dados_especificos') || !this.getPhaseDataByKey('dados_especificos')?.value) {
        return ''
      }
      const diagnosisYear = parseInt(this.getPhaseDataByKey('dados_especificos')?.ano_diagnostico_psoriase?.value)
      if (diagnosisYear) {
        const difference = new Date().getFullYear() - diagnosisYear

        if (difference === 0) {
          return ', diagnosticado nesse ano, '
        }

        if (difference === 1) {
          return `, há ${difference} ano, `
        }

        return `, há ${difference} anos, `
      }
      return ''
    },
    jointInvestigationPhaseData() {
      return this.getPhaseDataByKey('investigacao_articular')
    },
    psoriaticArthritisText() {
      if (!this.jointInvestigationPhaseData) {
        return ''
      }
      const value = this.jointInvestigationPhaseData?.diagnostico_artrite_psoriatica?.value

      if (value === 'NAO') {
        return ', sem artrite psoriática'
      } else if (value === 'SIM') {
        return ', com artrite psoriática'
      } else if (value === 'NÃO_SEI' && (parseInt(this.computedCaspar) > 2 || parseInt(this.computedPest) > 2)) {
        return ', com suspeita de artrite psoriática'
      }
      return ''
    },
  },
  methods: {
    getHypothesisText() {
      const age = this.$util.toAge(this.attendance.patient.birthday)

      const diagnosticYear = this.specificInfoPhaseData?.ano_diagnostico_psoriase?.value

      const comorbidity =
        !this.investigationPhaseData ||
        this.investigationPhaseData?.noDisease ||
        this.investigationPhaseData?.diseases?.length === 0
          ? 'Não apresenta comorbidades'
          : `${this.comorbiditiesText}`

      const familyHistory = !this.familyHistoryPhaseData ? 'Sem histórico familiar' : `${this.familyHistoryText}`

      const obese =
        this.$util.calcImc(this.attendance.patient.currentWeightKg, this.attendance.patient.currentHeightCm) >= 25

      const anxiety = this.anxietyScore >= 2 ? 'Suspeita de transtorno de ansiedade' : null
      const depression = this.depressionScore >= 2 ? 'Suspeita de transtorno depressivo' : null
      const topicTreatmentFailure =
        this.getPhaseDataByKey('areas_afetadas')?.medicamentos_previos_mais_de_3_meses === 'SIM_SEM_MELHORIA'

      const paragraphs = [
        `Paciente de ${age}, com diagnóstico de psoríase ${this.diseaseLevelText}${this.diagnosisTimeText} ${this.affectedAreasText}${this.psoriaticArthritisText} desde ${diagnosticYear}.`,
        '\nHistórico de medicamentos:',
        `    - Medicamentos atuais: ${this.currentMedicinesText};`,
        `    - DMARDs e sistêmicos não biológicos prévios: ${this.previousSistemicMedicinesText};`,
        `    - Imunobiológicos ou pequenas moléculas prévios: ${this.previousImunobiologicMedicinesText};`,
        `    - Tópicos prévios: ${this.previousTopicMedicinesText}.`,
        `    - Fototerápicos prévios: ${this.previousFototerapicMedicinesText}.`,
        `    - Anti-inflamatórios prévios: ${this.previousAntiinflamatoryMedicinesText}.\n`,
      ]

      if (topicTreatmentFailure) {
        paragraphs.push('Falha ao tratamento tópico por mais de 3 meses.\n')
      }

      paragraphs.push(`${comorbidity}.`)

      paragraphs.push(`\n${familyHistory}`)

      if (this.investigationPhaseData?.medicine) {
        paragraphs.push('Uso de: ' + this.investigationPhaseData.medicine)
      }

      if (obese) {
        paragraphs.push('Com obesidade.')
      }

      paragraphs.push(`${this.previousTuberculosisText}.`)
      paragraphs.push(`${this.tuberculosisRiskText}.`)

      if (anxiety) {
        paragraphs.push(`${anxiety}.`)
      }
      if (depression) {
        paragraphs.push(`${depression}.`)
      }

      paragraphs.push(`\n${this.healthPlanText}.`)

      paragraphs.push('\nSugiro para o tratamento do paciente acima:')
      paragraphs.push(
        'Controlar as comorbidades identificadas acima inclusive com acompanhamento em conjunto com o especialista se considerar necessário.',
      )
      paragraphs.push(
        'Na próxima etapa estão algumas opções de tratamento para a doença psoriásica desse paciente. Em caso de dúvidas, converse com um especialista através da INTERCONSULTA',
      )
      paragraphs.push('\nObservações importantes:')
      paragraphs.push(
        '- Medicamentos anti-TNF estão associados a um maior risco de reativação de tuberculose que anti-interleucinas. Existem recomendações de que pacientes com psoríase com indicação de imunobiológico e TUBERCULOSE LATENTE ou ALTO RISCO DE EXPOSIÇÃO PRÉVIA (contato com pessoa com tuberculose ativa, viagens para áreas de alto risco de tuberculose ou uso de imunossupressor atual ou prévio) não devem usar anti-TNF como primeira opção terapêutica (pág 9 e 10 do PCDT de psoríase e literatura em anexo nas referências)',
      )
      paragraphs.push(
        '- Os pacientes que já estiverem em uso de medicamento biológico para a psoríase deverão ser avaliados para manutenção do tratamento considerando a publicação do PCDT em 2019 (pag 39)',
      )

      return paragraphs.join('\n')
    },
    getPreviousMedicineText(type) {
      let previousMedicines = this.medicineHistory.filter(
        (m) => (m.periods.length === 0 || m.periods.some((p) => !!p.end)) && m.medicine.category.id === type,
      )

      if (previousMedicines.length === 0) {
        return 'nenhum'
      }

      return previousMedicines
        .map((m) => {
          if (m.periods.length === 0) {
            return `${m.medicine.name}`
          }

          let periods = m.periods
            .filter((p) => !!p.end)
            .map((p) => `de ${this.$util.toFormattedDate(p.start)} a ${this.$util.toFormattedDate(p.end)}`)
            .join(', ')

          return `${m.medicine.name} (${periods})`
        })
        .join(', ')
    },
  },
}
</script>

<style scoped></style>
